import React, { useCallback, useEffect, useState } from 'react';

function ResizableDivider({ 
  onResize, 
  onResizeVertical,
  minWidth = 250, 
  maxWidth = 600,
  minHeight = 150,
  maxHeight = 500,
  isMobile = false 
}) {
  const [isDragging, setIsDragging] = useState(false);

  const handleStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    
    if (isMobile && onResizeVertical) {
      // Vertical resizing - adjust map height based on Y position
      const headerHeight = 70; // Height of the header
      const newMapHeight = clientY - headerHeight;
      const clampedHeight = Math.min(Math.max(newMapHeight, minHeight), maxHeight);
      onResizeVertical(clampedHeight);
    } else if (onResize) {
      // Horizontal resizing - adjust sidebar width based on X position
      const windowWidth = window.innerWidth;
      const newSidebarWidth = windowWidth - clientX;
      const clampedWidth = Math.min(Math.max(newSidebarWidth, minWidth), maxWidth);
      onResize(clampedWidth);
    }
  }, [isDragging, isMobile, minWidth, maxWidth, minHeight, maxHeight, onResize, onResizeVertical]);

  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [handleMove]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      // Touch events
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      document.addEventListener('touchcancel', handleEnd);
      
      document.body.style.cursor = isMobile ? 'row-resize' : 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleTouchMove, handleEnd, isMobile]);

  return (
    <div
      className={`resizable-divider ${isDragging ? 'dragging' : ''} ${isMobile ? 'horizontal' : ''}`}
      onMouseDown={handleStart}
      onTouchStart={handleStart}
    >
      <div className="divider-handle" />
    </div>
  );
}

export default ResizableDivider;
